import React, { useState } from 'react';

import { Button, Spinner, Text } from '@change/ds-core-ui';

import { Translate } from '@change-corgi/core/react/i18n';

import { usePetitionDetailsPageData } from 'src/app/pages/petitionDetails/pageContext';
import { useEndorsements } from 'src/app/shared/components/endorsements';

import { EndorsementsList } from './components/EndorsementsList';
import { EndorsementsModal } from './components/EndorsementsModal';

export const EndorsementsContainer: React.FC = () => {
	const [isEndorsementsModalOpen, setIsEndorsementsModalOpen] = useState(false);
	const [endorsementIdToScrollTo, setEndorsementIdToScrollTo] = useState<string | null>(null);

	const handleOpenEndorsementsModal = (endorsementId?: string) => {
		setIsEndorsementsModalOpen(true);
		setEndorsementIdToScrollTo(endorsementId ?? null);
	};

	const handleCloseEndorsementsModal = () => {
		setIsEndorsementsModalOpen(false);
		setEndorsementIdToScrollTo(null);
	};

	const {
		data: { petition },
	} = usePetitionDetailsPageData();

	const {
		data: { endorsements, isLoading, error },
	} = useEndorsements({ petitionSlugOrId: petition.id });

	if (isLoading) {
		return <Spinner className="mx-auto my-16" />;
	}

	if (error) {
		return <div className="text-typography-weak">Failed to load endorsements</div>;
	}

	if (endorsements.length === 0) {
		return null;
	}

	return (
		<div className="mt-20 mb-20 flex flex-col">
			<Text className="text-size-heading-small md:text-size-heading-large">
				<Translate value="corgi.petition.details.endorsements.heading" />
			</Text>
			<Text variant="body" className="text-size-body sm:text-size-body-xl mt-5">
				<Translate value="corgi.petition.details.endorsements.subheading" />
			</Text>
			<div className="mt-10 mb-6">
				<EndorsementsList onOpenEndorsementsModal={handleOpenEndorsementsModal} endorsements={[...endorsements]} />
			</div>

			<EndorsementsModal
				isOpen={isEndorsementsModalOpen}
				onClose={handleCloseEndorsementsModal}
				setIsOpen={setIsEndorsementsModalOpen}
				endorsements={[...endorsements]}
				endorsementIdToScrollTo={endorsementIdToScrollTo}
				onScrolledToEndorsement={() => setEndorsementIdToScrollTo(null)}
			/>

			<Button
				variant="secondary"
				size="small"
				data-qa="view-all-endorsements-button"
				data-testid="view-all-endorsements-button"
				onClick={() => handleOpenEndorsementsModal()}
			>
				<Translate value="corgi.petition.details.endorsements.view_all_button" />
			</Button>
		</div>
	);
};
